import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Project, Episode, ScriptAnalysis, AnalysisCharacter, AnalysisScene, AnalysisProp, ProjectMember, User, AssetSearchTask, AnalysisMode } from '../types';
import {
  getProject, listEpisodes, retryAnalysis, getScriptAnalysis,
  addAnalysisElement,
  getActiveImageTasks,
  listProjectMembers,
  getAssetSearchTasks,
  triggerAssetSearch,
  manualTriggerAssetSearch,
  generateAssetKeywords,
  isApiError,
  selectSearchRefImages,
  selectBaseRefImages,
} from '../api';
import HeaderProjectNav from '../components/HeaderProjectNav';
import { useHeaderSlot } from '../components/HeaderSlotContext';
import CostFloatingPill from '../components/CostFloatingPill';
import EmptyState from '../components/EmptyState';
import { useToast } from '../components/Toast';
import { useIsMobile } from '../utils/useIsMobile';
import { AssetRenameCategory } from '../api/assetRename';
import {
  createAssetShareLink,
} from '../api/assetShare';
import { buildActiveImageTaskListMap, buildFailedTaskListMap, ActiveImageTask, SlotFailure } from './project-detail/utils/activeImageTasks';
import { MAX_TOTAL_REF_IMAGES, parseRefImages } from './project-detail/utils/editDefaults';
import { newCharacterState, newSceneState, newPropState } from './project-detail/utils/addStateForm';

import {
  AddingNewElement,
  ElementType,
  tryParseJSON,
} from './project-detail/components/AnalysisElements';
import {
  collectAssetEpisodes,
  filterGroupsByEpisodes,
  groupAnalysisAssets,
} from './project-detail/utils/analysisGrouping';
import { LatestAuthorityRequestCoordinator, mergeAnalysisEntities } from './project-detail/utils/analysisRefresh';
import { useAssetSortMode } from './project-detail/hooks/useAssetSortMode';
import {
  getActiveSearchDetails,
  getSearchCounts,
  getSearchSelectGroups,
  getSearchSummary,
  isKeywordPlaceholderTimedOut,
  SearchSelectModalState,
} from './project-detail/utils/assetSearchDerived';
import { useProjectAssetReviews } from './project-detail/hooks/useProjectAssetReviews';
import { useProjectAssetComments } from './project-detail/hooks/useProjectAssetComments';
import { useAssetShareHistory } from './project-detail/hooks/useAssetShareHistory';
import { useOpenElementTarget, useScrollToOpenElement } from './project-detail/hooks/useOpenElementTarget';
import { useAddElementTarget, useConsumeAddElementTarget } from './project-detail/hooks/useAddElementTarget';
import { useThumbSeenRefresh } from './project-detail/hooks/useProjectDetailUiEvents';
import ScriptFormatGuideModal from './project-detail/components/ScriptFormatGuideModal';
import EditProjectDialog from './project-detail/components/EditProjectDialog';
import StyleConfigDialog from './project-detail/components/StyleConfigDialog';
import { useProjectConfigState } from './project-detail/hooks/useProjectConfigState';
import { useForcedConfig } from './project-detail/hooks/useForcedConfig';
import { shouldPollEpisode } from './episode-detail/utils/episodePolling';
import { shouldPollAnalysisStatus } from './project-detail/utils/analysisPolling';
import ProjectConfirmDialogs from './project-detail/components/ProjectConfirmDialogs';
import ProjectInfoSummary from './project-detail/components/ProjectInfoSummary';
import ExternalAnalysisSection from './project-detail/components/ExternalAnalysisSection';
import ProjectDetailAuxiliaryModals from './project-detail/components/ProjectDetailAuxiliaryModals';
import ProjectAnalysisPanel from './project-detail/components/ProjectAnalysisPanel';
import { buildCharacterVoiceMap } from '../utils/characterVoice';
const ProjectDetail: React.FC = () => {
  const { t } = useTranslation('projectDetail');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const openElement = useOpenElementTarget();
  const addElementTarget = useAddElementTarget();
  const [project, setProject] = useState<Project | null>(null);
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isBaseConfigOpen, setIsBaseConfigOpen] = useState(false);

  const [members, setMembers] = useState<ProjectMember[]>([]);
  const currentUser: User | null = JSON.parse(localStorage.getItem('user') || 'null');
  const [polling, setPolling] = useState(false);
  const [episodePolling, setEpisodePolling] = useState(false);
  const [analysis, setAnalysis] = useState<ScriptAnalysis | null>(null);
  const [analysisAuthorityRevision, setAnalysisAuthorityRevision] = useState(0);
  const analysisAuthorityCoordinatorRef = useRef(new LatestAuthorityRequestCoordinator());
  useLayoutEffect(() => {
    const scope = id ?? '';
    const coordinator = analysisAuthorityCoordinatorRef.current;
    coordinator.activate(scope);
    return () => coordinator.deactivate(scope);
  }, [id]);
  const [retryingAnalysis, setRetryingAnalysis] = useState(false);
  const [cancellingAnalysis, setCancellingAnalysis] = useState(false);
  const [externalTaskId, setExternalTaskId] = useState<string | null>(null);
  // note: track the last failed task so the panel can offer a retry. We could
  // surface a richer error log here later, but a single id + reason is enough
  // to make retryExternalAnalysis() reachable from the current UI.
  const [lastFailedExternalTaskId, setLastFailedExternalTaskId] = useState<string | null>(null);
  const [lastFailedExternalReason, setLastFailedExternalReason] = useState<string | null>(null);
  const [analysisTab, setAnalysisTab] = useState<'character' | 'scene' | 'prop'>(() => {
    if (addElementTarget) return addElementTarget;
    if (openElement?.type === 'scene') return 'scene';
    if (openElement?.type === 'prop') return 'prop';
    return 'character';
  });
  const [filterEpisodes, setFilterEpisodesState] = useState<number[]>(() => {
    const saved = localStorage.getItem(`analysisFilterEpisode:${id}`);
    if (!saved) return [];
    try {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) return parsed.filter(n => Number.isFinite(n));
    } catch {
      // 旧版本存的是单个数字字符串，迁移为数组
    }
    const legacy = Number(saved);
    return Number.isFinite(legacy) ? [legacy] : [];
  });
  const setFilterEpisodes = useCallback((next: number[]) => {
    setFilterEpisodesState(next);
    if (next.length === 0) localStorage.removeItem(`analysisFilterEpisode:${id}`);
    else localStorage.setItem(`analysisFilterEpisode:${id}`, JSON.stringify(next));
  }, [id]);
  const [assetSortMode, setAssetSortMode] = useAssetSortMode();
  const [chars, setChars] = useState<AnalysisCharacter[]>([]);
  const [scenes, setScenes] = useState<AnalysisScene[]>([]);
  const [props, setProps] = useState<AnalysisProp[]>([]);
  // elementName -> taskId，用于展示后端 auto-submit 任务的生成中状态
  const [activeTaskMap, setActiveTaskMap] = useState<Map<string, ActiveImageTask[]>>(new Map());
  const [failedTaskMap, setFailedTaskMap] = useState<Map<string, SlotFailure[]>>(new Map());
  const [baseActiveTaskMap, setBaseActiveTaskMap] = useState<Map<string, ActiveImageTask[]>>(new Map());
  const [baseFailedTaskMap, setBaseFailedTaskMap] = useState<Map<string, SlotFailure[]>>(new Map());
  const resolvedImageTaskIdsRef = useRef<Set<string>>(new Set());
  useThumbSeenRefresh();
  const episodeMap = useMemo(() => new Map(episodes.map(ep => [ep.index, ep.id])), [episodes]);
  // Sorted list of episode options for the "appears in" multi-select (used by
  // the new-asset dialog and the per-asset edit modal).
  const availableEpisodeOptions = useMemo(
    () =>
      [...episodes]
        .sort((a, b) => a.index - b.index)
        .map(ep => ({ episodeIndex: ep.index, title: ep.name })),
    [episodes],
  );

  // 头部导航 toggle 跳转目标：分镜列表是按集的，优先回到上次看过的那一集，
  // 否则回退到第一集；项目还没有任何剧集时为 null（toggle 会置灰）。
  const storyboardEpisodeId = useMemo(() => {
    if (!id || episodes.length === 0) return null;
    const last = localStorage.getItem(`lastEpisode:${id}`);
    if (last && episodes.some(ep => ep.id === last)) return last;
    return [...episodes].sort((a, b) => a.index - b.index)[0]?.id ?? null;
  }, [id, episodes]);
  // 硬强制未完成（缺风格或缺剧本）时隐藏顶栏配置按钮，避免绕过强制弹窗再调出同系列弹窗。
  // 等价于 needsStyle || needsScript（useForcedConfig 在此之后才调用，故就地内联同一判定）。
  const configForced = !!project && !(project.baseConfigCompleted && !!project.script && project.script.status !== 'failed');
  // Step4「请上传剧本」态（风格已配、剧本未传）：此时前两步设置已完成，放出顶栏「项目设置/风格设置」
  // 供用户回看修改（等价于 needsScript）。
  const atScriptStep = !!project && !!project.baseConfigCompleted && !(!!project.script && project.script.status !== 'failed');
  const isMobile = useIsMobile();
  useHeaderSlot(
    project && id
      ? <HeaderProjectNav
          projectId={id}
          active="assets"
          storyboardEpisodeId={storyboardEpisodeId}
          project={project}
          currentUser={currentUser}
          memberCount={members.length}
          onEditProject={() => setIsEditing(true)}
          onStyleConfig={() => setIsBaseConfigOpen(true)}
          forced={configForced}
          scriptStep={atScriptStep}
        />
      : null,
    project && id && !isMobile ? <CostFloatingPill projectId={id} variant="inline" /> : null,
    [id, storyboardEpisodeId, !!project, members.length, configForced, atScriptStep, isMobile],
  );
  // 新增要素表单
  const [addingNewChar, setAddingNewChar] = useState<AddingNewElement | null>(null);
  const [addingNewScene, setAddingNewScene] = useState<AddingNewElement | null>(null);
  const [addingNewProp, setAddingNewProp] = useState<AddingNewElement | null>(null);
  useConsumeAddElementTarget(addElementTarget, {
    setAnalysisTab,
    setAddingNewChar,
    setAddingNewScene,
    setAddingNewProp,
  });

  // ConfirmDialog state for re-analyze
  const [confirmReanalyzeOpen, setConfirmReanalyzeOpen] = useState(false);
  const [confirmReanalyzeStep, setConfirmReanalyzeStep] = useState<1 | 2>(1);

  // ConfirmDialog state for generate image prompts (B2)
  const [confirmGenPromptsOpen, setConfirmGenPromptsOpen] = useState(false);
  const [generatingPrompts, setGeneratingPrompts] = useState(false);


  // 资产禁用相关状态
  const [disableAssetPending, setDisableAssetPending] = useState<{ category: number; name: string } | null>(null);
  const [disableAssetAffectedCount, setDisableAssetAffectedCount] = useState(0);
  const [disableAssetAffectedDetails, setDisableAssetAffectedDetails] = useState<Array<{ episodeIndex: number; shotIndex: number }>>([]);
  const [disableAssetStep, setDisableAssetStep] = useState<1 | 2>(1); // 1=影响提示, 2=最终确认
  const [disableAssetLoading, setDisableAssetLoading] = useState(false);

  // 资产重命名对话框目标
  const [renameTarget, setRenameTarget] = useState<{ category: AssetRenameCategory; name: string } | null>(null);
  const [commentTarget, setCommentTarget] = useState<{ category: AssetRenameCategory; name: string } | null>(null);

  // 左侧导航高亮：scroll spy
  const [activeNavId, setActiveNavId] = useState<string | null>(null);

  // Mobile: selected asset for drawer (isMobile is declared above, before useHeaderSlot)
  const [mobileDrawerAsset, setMobileDrawerAsset] = useState<{ type: 'character' | 'scene' | 'prop'; name: string } | null>(null);

  // Asset ref image search
  const [searchTasks, setSearchTasks] = useState<AssetSearchTask[]>([]);
  const [triggeringSearch, setTriggeringSearch] = useState(false);
  const [searchSelectModal, setSearchSelectModal] = useState<SearchSelectModalState | null>(null);

  // 从 searchTasks 动态计算弹窗 groups（轮询更新后弹窗自动刷新）
  const searchSelectGroups = useMemo(() => getSearchSelectGroups(searchTasks, searchSelectModal), [searchSelectModal, searchTasks]);

  const searchSelectImages = useMemo(() => searchSelectGroups.flatMap(g => g.images), [searchSelectGroups]);
  const [searchSelectedUrls, setSearchSelectedUrls] = useState<string[]>([]);
  const [savingSearchSelection, setSavingSearchSelection] = useState(false);
  const [searchLightboxIndex, setSearchLightboxIndex] = useState<number | null>(null);
  const {
    searchingCount,
    searchQueuedCount,
    searchActiveCount,
    isGeneratingKeywords,
    searchIsActive,
  } = useMemo(() => getSearchCounts(searchTasks), [searchTasks]);
  const [confirmResearchOpen, setConfirmResearchOpen] = useState(false);
  const [confirmCategorySearch, setConfirmCategorySearch] = useState<{ cat: number; label: string } | null>(null);
  const [confirmBatchSearch, setConfirmBatchSearch] = useState<{ category: number; label: string; count: number } | null>(null);
  const searchDetailExpanded = false;

  // --- Asset share states ---
  const [assetShareModal, setAssetShareModal] = useState(false);
  const [assetShareForm, setAssetShareForm] = useState({ label: '', hours: 72, maxViews: 0 });
  const [assetShareLoading, setAssetShareLoading] = useState(false);
  const {
    assetShareHistory,
    backToShareHistory,
    closeShareHistory,
    loadShareComments,
    openShareHistory,
    selectedShareComments,
    selectedShareImageMap,
    selectedShareLink,
    shareCommentsError,
    shareCommentsLoading,
    shareHistoryError,
    shareHistoryLoading,
    showShareHistory,
  } = useAssetShareHistory(id, {
    loadHistoryFailed: t('assetShare.loadHistoryFailed'),
    loadCommentsFailed: t('assetShare.loadCommentsFailed'),
  });

  const copyAssetShareLink = useCallback((code: string) => {
    const url = `${window.location.origin}/asset-share/${code}`;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => toast.success(t('assetShare.linkCopied')));
    } else {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success(t('assetShare.linkCopied'));
    }
  }, [t, toast]);

  const handleCreateAssetShare = useCallback(async () => {
    setAssetShareLoading(true);
    try {
      if (!id) return;
      const { code } = await createAssetShareLink(id, {
        label: assetShareForm.label.trim(),
        hours: assetShareForm.hours,
        maxViews: assetShareForm.maxViews,
      });
      copyAssetShareLink(code);
      setAssetShareModal(false);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : t('assetShare.generateFailed'));
    } finally {
      setAssetShareLoading(false);
    }
  }, [assetShareForm, copyAssetShareLink, id, t, toast]);

  // 正在进行中的搜索任务（queued + searching），按资产名分组
  const activeSearchDetails = useMemo(
    () => getActiveSearchDetails(searchTasks, { 1: t('analysis.character'), 2: t('analysis.scene'), 3: t('analysis.prop') }),
    [searchTasks, t],
  );

  // 搜索完成状态摘要
  const searchSummary = useMemo(() => getSearchSummary(searchTasks), [searchTasks]);

  // 占位记录超时检测（5分钟）
  const placeholderTimedOut = useMemo(
    () => isKeywordPlaceholderTimedOut(searchTasks, isGeneratingKeywords),
    [isGeneratingKeywords, searchTasks],
  );

  // 搜索任务进行中时轮询刷新（generating_keywords 阶段也轮询）
  useEffect(() => {
    if (!id || !searchIsActive) return;
    const interval = setInterval(async () => {
      try {
        const tasks = await getAssetSearchTasks(id);
        setSearchTasks(tasks || []);
      } catch { /* ignore */ }
    }, isGeneratingKeywords ? 5000 : 10000); // 关键词生成阶段 5 秒轮询，搜图阶段 10 秒
    return () => clearInterval(interval);
  }, [id, searchIsActive, isGeneratingKeywords]);

  const characterVoiceMap = useMemo(() => buildCharacterVoiceMap(chars), [chars]);
  const charGroups = useMemo(() => groupAnalysisAssets(chars, assetSortMode), [chars, assetSortMode]);
  const sceneGroups = useMemo(() => groupAnalysisAssets(scenes, assetSortMode), [scenes, assetSortMode]);
  const propGroups = useMemo(() => groupAnalysisAssets(props, assetSortMode), [props, assetSortMode]);

  // 所有资产中出现的剧集编号（去重排序）
  const allAssetEpisodes = useMemo(() => collectAssetEpisodes(chars, scenes, props), [chars, scenes, props]);

  // 按剧集筛选后的分组（同时筛掉不属于该集的状态）
  const filteredCharGroups = filterEpisodes.length === 0 ? charGroups : filterGroupsByEpisodes(charGroups, filterEpisodes);
  const filteredSceneGroups = filterEpisodes.length === 0 ? sceneGroups : filterGroupsByEpisodes(sceneGroups, filterEpisodes);
  const filteredPropGroups = filterEpisodes.length === 0 ? propGroups : filterGroupsByEpisodes(propGroups, filterEpisodes);

  // Scroll spy: 滚动时高亮最靠近视口顶部的资产卡片
  useEffect(() => {
    const groups = analysisTab === 'character' ? filteredCharGroups
      : analysisTab === 'scene' ? filteredSceneGroups : filteredPropGroups;
    const ids = groups.map(g => `elem-${analysisTab}-${g.name}`);
    if (ids.length === 0) { setActiveNavId(null); return; }

    let rafId = 0;
    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const headerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 58;
        const threshold = headerH + 20;
        let bestId: string | null = null;
        let bestDist = Infinity;
        for (const id of ids) {
          const el = document.getElementById(id);
          if (!el) continue;
          const rect = el.getBoundingClientRect();
          // 卡片底部在视口顶部以下（还未完全滚过），且离阈值线最近
          if (rect.bottom > threshold) {
            const dist = Math.abs(rect.top - threshold);
            if (dist < bestDist) {
              bestDist = dist;
              bestId = id;
            }
          }
        }
        setActiveNavId(bestId);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // 初始化
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(rafId); };
  }, [analysisTab, filteredCharGroups, filteredSceneGroups, filteredPropGroups]);

  const loadProject = useCallback(async (projectId: string) => {
    try {
      const data = await getProject(projectId);
      setProject(data);
      // Hydrate externalTaskId from server-side ActiveAnalysisTaskID so a page
      // reload during in-flight analysis re-attaches the panel instead of showing
      // the "开始分析" button. Only hydrate when local state is null (don't clobber
      // a fresh Trigger response that already populated this).
      setExternalTaskId(curr => curr ?? data.activeAnalysisTaskId ?? null);
      if (data.lastFailedExtTaskId) {
        setLastFailedExternalTaskId(prev => prev ?? data.lastFailedExtTaskId ?? null);
        setLastFailedExternalReason(prev => prev ?? data.lastFailedExtReason ?? null);
      }
      // Load members
      listProjectMembers(projectId).then(setMembers).catch(() => {});

      if (data.script &&
          (data.script.status === 'uploading_to_tos' ||
           data.script.status === 'uploaded' ||
           data.script.status === 'splitting' ||
           data.script.status === 'downloading_episodes' ||
           data.script.status === 'uploading_episodes_to_tos')) {
        setPolling(true);
      } else {
        setPolling(false);
      }
    } catch (err) {
      console.error('Failed to load project:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 自包含配置态（项目设置 + 风格设置共用）。保存成功后刷新项目。
  const cfg = useProjectConfigState(project, { onSaved: () => { if (id) loadProject(id); } });

  // 硬强制完成度判定 + 强制剧本上传（与剧本信息页共用同一 hook，§3-②）。
  const { needsStyle, needsScript, forcedUploading, handleForcedScriptUpload } = useForcedConfig(project, {
    projectId: id,
    // 资产页完成强制上传后，把用户带到「剧本信息」页（主入口）。
    onUploaded: () => { if (id) navigate(`/projects/${id}/script-summary`); },
    onError: (msg) => toast.error(msg),
    uploadFailedMessage: t('script.uploadFailed'),
  });

  const loadEpisodes = useCallback(async (projectId: string) => {
    try {
      const data = await listEpisodes(projectId);
      setEpisodes(data);
      // 与单集页同源的 shouldPollEpisode 统一口径：pending 仅在 promptGenerationStatus==='generating'
      // 时才算进行中（否则「等待资产分析/等待生成分镜」会被误判为进行中而长期空轮询）；
      // 终态失败（normalizing_failed / resolving_pronouns_failed）不轮询，需用户手动重试。
      setEpisodePolling(data.some(shouldPollEpisode));
    } catch (err) {
      console.error('Failed to load episodes:', err);
    }
  }, []);

  const loadAnalysis = useCallback(async (projectId: string) => {
    const authorityLoaded = await analysisAuthorityCoordinatorRef.current.run(
      projectId,
      () => getScriptAnalysis(projectId),
      data => {
        // 防止轮询将本地已确认的 cancelled 状态回退为 analyzing（goroutine 尚未退出时服务端仍返回 analyzing）
        setAnalysis(prev => {
          if (prev?.status === 'cancelled' && data.status === 'analyzing') return prev;
          if (data.status === 'not_started') return null;
          return data;
        });
        if (data.status === 'completed' || data.status === 'extracted') {
          setChars(prev => mergeAnalysisEntities(prev, tryParseJSON<AnalysisCharacter[]>(data.characters)));
          setScenes(prev => mergeAnalysisEntities(prev, tryParseJSON<AnalysisScene[]>(data.scenes)));
          setProps(prev => mergeAnalysisEntities(prev, tryParseJSON<AnalysisProp[]>(data.props)));
          setAnalysisAuthorityRevision(revision => revision + 1);
        }
        return true;
      },
      error => {
        if (!isApiError(error) || error.status !== 404) {
          console.error('Failed to load analysis authority:', error);
        }
        return false;
      },
    );
    if (!analysisAuthorityCoordinatorRef.current.isActive(projectId)) return false;
    // 查询后端正在进行中的生图任务，更新 activeTaskMap
    try {
      const { tasks, failed, tasksBase, failedBase } = await getActiveImageTasks(projectId);
      if (!analysisAuthorityCoordinatorRef.current.isActive(projectId)) return false;
      setActiveTaskMap(buildActiveImageTaskListMap(tasks, resolvedImageTaskIdsRef.current));
      setFailedTaskMap(buildFailedTaskListMap(failed ?? [], resolvedImageTaskIdsRef.current));
      setBaseActiveTaskMap(buildActiveImageTaskListMap(tasksBase ?? [], resolvedImageTaskIdsRef.current));
      setBaseFailedTaskMap(buildFailedTaskListMap(failedBase ?? [], resolvedImageTaskIdsRef.current));
    } catch {
      // ignore
    }
    if (!analysisAuthorityCoordinatorRef.current.isActive(projectId)) return false;
    // 加载参考图搜索任务
    try {
      const tasks = await getAssetSearchTasks(projectId);
      if (!analysisAuthorityCoordinatorRef.current.isActive(projectId)) return false;
      setSearchTasks(tasks || []);
    } catch {
      // ignore
    }
    if (!analysisAuthorityCoordinatorRef.current.isActive(projectId)) return false;
    return authorityLoaded;
  }, []);

  const handleImageTaskResolved = useCallback((taskId: string) => {
    resolvedImageTaskIdsRef.current.add(taskId);
    const dropFromListMap = <T extends { taskId: string }>(
      prev: Map<string, T[]>,
    ): Map<string, T[]> => {
      let changed = false;
      const next = new Map(prev);
      next.forEach((list, key) => {
        const filtered = list.filter(t => t.taskId !== taskId);
        if (filtered.length !== list.length) {
          changed = true;
          if (filtered.length === 0) next.delete(key);
          else next.set(key, filtered);
        }
      });
      return changed ? next : prev;
    };
    setActiveTaskMap(prev => dropFromListMap(prev));
    setFailedTaskMap(prev => dropFromListMap(prev));
    setBaseActiveTaskMap(prev => dropFromListMap(prev));
    setBaseFailedTaskMap(prev => dropFromListMap(prev));
  }, []);

  useEffect(() => {
    if (id) {
      loadProject(id);
      loadEpisodes(id);
      loadAnalysis(id);
    }
  }, [id, loadProject, loadEpisodes, loadAnalysis]); // eslint-disable-line react-hooks/exhaustive-deps

  // 真人剧：加载项目级审核记录
  const isRealPerson = (project?.projectType || 'animation') === 'real_person';
  const { loadProjectReviews, reviewStatusMap } = useProjectAssetReviews(id, isRealPerson);
  const { pendingCommentCountMap, reloadAssetComments } = useProjectAssetComments(id, true);

  useEffect(() => {
    if ((!polling && !episodePolling) || !id) return;

    const interval = setInterval(() => {
      if (polling) loadProject(id);
      loadEpisodes(id);
      if (polling) loadAnalysis(id);
    }, 2000);

    return () => clearInterval(interval);
  }, [polling, episodePolling, id, loadProject, loadEpisodes, loadAnalysis]);

  // 单独轮询分析状态（分析在剧本拆分完成后才开始，不依赖 polling）
  useEffect(() => {
    if (!id || !analysis || !shouldPollAnalysisStatus(analysis.status)) return;

    const interval = setInterval(() => {
      loadAnalysis(id);
      // 同步刷新剧集列表（pending_normalization 需要更新改写进度，其他状态下调用也无害）
      loadEpisodes(id);
    }, 3000);

    return () => clearInterval(interval);
  }, [id, analysis?.status, loadAnalysis, loadEpisodes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id || (activeTaskMap.size === 0 && baseActiveTaskMap.size === 0)) return;
    const interval = setInterval(() => loadAnalysis(id), 5000);
    return () => clearInterval(interval);
  }, [id, activeTaskMap.size, baseActiveTaskMap.size, loadAnalysis]);

  useScrollToOpenElement(openElement, [chars, scenes, props]);

  const handleElementUpdate = (type: ElementType, name: string, imagePrompt: string, imageUrls?: string[], refImageUrls?: string[], selectedImageUrl?: string, promptIncludesPrefix?: boolean) => {
    const sceneKey = (s: AnalysisScene) => s.state ? `${s.name}|${s.state}` : s.name;
    const propKey = (p: AnalysisProp) => p.state ? `${p.name}|${p.state}` : p.name;
    const withPromptPrefix = <T extends { promptIncludesPrefix?: boolean }>(item: T): T =>
      promptIncludesPrefix === undefined ? item : { ...item, promptIncludesPrefix };
    if (type === 'character') {
      setChars(prev => prev.map(c => {
        const key = c.state ? `${c.name}|${c.state}` : c.name;
        return key === name ? withPromptPrefix({ ...c, imagePrompt, imageUrls, refImageUrls, selectedImageUrl }) : c;
      }));
    } else if (type === 'scene') {
      setScenes(prev => prev.map(s => sceneKey(s) === name ? withPromptPrefix({ ...s, imagePrompt, imageUrls, refImageUrls, selectedImageUrl }) : s));
    } else {
      setProps(prev => prev.map(p => propKey(p) === name ? withPromptPrefix({ ...p, imagePrompt, imageUrls, refImageUrls, selectedImageUrl }) : p));
    }
  };

  // After EpisodeEditModal saves, lift the new episode list into local state so
  // EpisodeBadges re-renders without requiring a full analysis refetch. Matched
  // by composite (name, state) — state is '' for single-state assets.
  const handleElementEpisodesUpdate = (
    type: ElementType,
    rawName: string,
    state: string,
    nextEpisodes: number[],
  ) => {
    const matches = (elem: { name: string; state?: string }) =>
      elem.name === rawName && (elem.state || '') === state;
    if (type === 'character') {
      setChars(prev => prev.map(c => (matches(c) ? { ...c, episodes: nextEpisodes } : c)));
    } else if (type === 'scene') {
      setScenes(prev => prev.map(s => (matches(s) ? { ...s, episodes: nextEpisodes } : s)));
    } else {
      setProps(prev => prev.map(p => (matches(p) ? { ...p, episodes: nextEpisodes } : p)));
    }
  };

  // 手动触发参考图搜索（可指定分类：1=人物 2=场景 3=道具，不传则全量）
  const handleTriggerSearch = async (category?: number) => {
    if (!id || triggeringSearch || (searchIsActive && !placeholderTimedOut)) return;
    setTriggeringSearch(true);
    try {
      await triggerAssetSearch(id, category);
      // 立即刷新任务列表（此时占位记录已存在）
      const tasks = await getAssetSearchTasks(id);
      setSearchTasks(tasks || []);
    } catch (err: any) {
      toast.error(err.message || t('analysis.searchTriggerFailed'));
    } finally {
      setTriggeringSearch(false);
    }
  };

  // 按分类手动触发搜索（经二次确认后调用）
  const handleBatchSearchConfirmed = async (category: number) => {
    if (!id) return;
    try {
      await manualTriggerAssetSearch(id, { category });
      const tasks = await getAssetSearchTasks(id);
      setSearchTasks(tasks || []);
    } catch (err: any) {
      toast.error(err.message || t('analysis.searchTriggerFailed'));
    }
  };

  // 打开搜索结果选择弹窗
  const openSearchSelectModal = (elementType: string, elementName: string, state: string, currentRefCount: number) => {
    const categoryMap: Record<string, number> = { character: 1, scene: 2, prop: 3 };
    const category = categoryMap[elementType] || 0;
    const hasAny = searchTasks.some(t =>
      t.category === category && t.assetName === elementName && t.assetState === (state || '')
    );
    if (!hasAny) {
      toast.warning(t('searchModal.noSearchTasks'));
      return;
    }
    setSearchSelectModal({ elementType, elementName, state, currentRefCount, maxTotal: 6 });
    setSearchSelectedUrls([]);
    setSearchLightboxIndex(null);
  };

  // 确认选择搜索结果图片
  const handleConfirmSearchSelect = async () => {
    if (!id || !searchSelectModal || searchSelectedUrls.length === 0) return;
    setSavingSearchSelection(true);
    try {
      if (searchSelectModal.state === '__base__') {
        // 基础图模式：添加到 baseRefImages
        await selectBaseRefImages(
          id,
          searchSelectModal.elementType,
          searchSelectModal.elementName,
          searchSelectedUrls,
        );
      } else {
        // 状态图模式：添加到 refImageUrls
        await selectSearchRefImages(
          id,
          searchSelectModal.elementType,
          searchSelectModal.elementName,
          searchSelectModal.state,
          searchSelectedUrls,
        );
      }
      toast.success(t('searchModal.addedRefImages', { count: searchSelectedUrls.length }));
      setSearchSelectModal(null);
      // 刷新分析数据以更新
      loadAnalysis(id);
    } catch (err: any) {
      toast.error(err.message || t('searchModal.selectRefFailed'));
    } finally {
      setSavingSearchSelection(false);
    }
  };

  // 获取某个资产的搜索结果图片数量
  const getSearchImageCount = (elementType: string, elementName: string, state: string): number => {
    const categoryMap: Record<string, number> = { character: 1, scene: 2, prop: 3 };
    const category = categoryMap[elementType] || 0;
    const matched = searchTasks.filter(t =>
      t.category === category && t.assetName === elementName && t.assetState === (state || '')
    );
    return matched.reduce((sum, t) => sum + (t.parsedImageUrls?.length || 0), 0);
  };

  // 获取某个资产所有状态的搜索结果图片总数
  const getSearchImageCountAllStates = (elementType: string, elementName: string): number => {
    const categoryMap: Record<string, number> = { character: 1, scene: 2, prop: 3 };
    const category = categoryMap[elementType] || 0;
    const matched = searchTasks.filter(t =>
      t.category === category && t.assetName === elementName
    );
    return matched.reduce((sum, t) => sum + (t.parsedImageUrls?.length || 0), 0);
  };

  // 获取某个资产的搜索任务进度 (completedCount/totalCount)
  const getSearchTaskProgress = (elementType: string, elementName: string, state: string): { completed: number; total: number } => {
    const categoryMap: Record<string, number> = { character: 1, scene: 2, prop: 3 };
    const category = categoryMap[elementType] || 0;
    const matched = searchTasks.filter(t =>
      t.category === category && t.assetName === elementName && t.assetState === (state || '') && t.category !== 0
    );
    const completed = matched.filter(t => t.status === 'completed' || t.status === 'failed').length;
    return { completed, total: matched.length };
  };

  // 获取某个资产所有状态的搜索任务进度
  const getSearchTaskProgressAllStates = (elementType: string, elementName: string): { completed: number; total: number } => {
    const categoryMap: Record<string, number> = { character: 1, scene: 2, prop: 3 };
    const category = categoryMap[elementType] || 0;
    const matched = searchTasks.filter(t =>
      t.category === category && t.assetName === elementName && t.category !== 0
    );
    const completed = matched.filter(t => t.status === 'completed' || t.status === 'failed').length;
    return { completed, total: matched.length };
  };

  const [generatingKeywordsKeys, setGeneratingKeywordsKeys] = useState<Set<string>>(new Set());

  const getKeywordStatus = (elementType: string, assetName: string, assetState: string): 'none' | 'generating' | 'pending' | 'searching' | 'completed' | 'failed' => {
    const key = `${elementType}:${assetName}:${assetState || ''}`;
    if (generatingKeywordsKeys.has(key)) return 'generating';
    const categoryMap: Record<string, number> = { character: 1, scene: 2, prop: 3 };
    const category = categoryMap[elementType] || 0;
    const matched = searchTasks.filter(t =>
      t.category === category && t.assetName === assetName && t.assetState === (assetState || '')
    );
    if (matched.length === 0) return 'none';
    if (matched.some(t => t.status === 'generating_keywords')) return 'generating';
    if (matched.some(t => t.status === 'searching' || t.status === 'queued')) return 'searching';
    if (matched.every(t => t.status === 'completed')) return 'completed';
    if (matched.some(t => t.status === 'failed')) return 'failed';
    if (matched.some(t => t.status === 'pending')) return 'pending';
    return 'none';
  };

  const handleGenerateKeywords = async (elementType: string, assetName: string, assetState: string, category: number) => {
    if (!id) return;
    const key = `${elementType}:${assetName}:${assetState || ''}`;
    setGeneratingKeywordsKeys(prev => new Set(prev).add(key));
    try {
      await generateAssetKeywords(id, { category, assetName, assetState });
      const tasks = await getAssetSearchTasks(id);
      setSearchTasks(tasks || []);
    } catch (err: any) {
      toast.error(err.message || t('analysis.searchTriggerFailed'));
    } finally {
      setGeneratingKeywordsKeys(prev => { const next = new Set(prev); next.delete(key); return next; });
    }
  };

  const handleSubmitSearch = async (elementType: string, assetName: string, assetState: string) => {
    if (!id) return;
    const categoryMap: Record<string, number> = { character: 1, scene: 2, prop: 3 };
    const category = categoryMap[elementType] || 0;
    const matched = searchTasks.filter(t =>
      t.category === category && t.assetName === assetName && t.assetState === (assetState || '') && t.status === 'pending'
    );
    if (matched.length === 0) return;
    try {
      await manualTriggerAssetSearch(id, { taskIds: matched.map(t => t.id) });
      const tasks = await getAssetSearchTasks(id);
      setSearchTasks(tasks || []);
    } catch (err: any) {
      toast.error(err.message || t('analysis.searchTriggerFailed'));
    }
  };

  // 打开搜索结果选择弹窗（所有状态合并，用于基础图）
  const openSearchSelectModalAllStates = (elementType: string, elementName: string, currentRefCount: number) => {
    const categoryMap: Record<string, number> = { character: 1, scene: 2, prop: 3 };
    const category = categoryMap[elementType] || 0;
    const hasAny = searchTasks.some(t =>
      t.category === category && t.assetName === elementName
    );
    if (!hasAny) {
      toast.warning(t('searchModal.noSearchTasks'));
      return;
    }
    const refCountMap: Record<string, number> = {
      character: Math.max(0, MAX_TOTAL_REF_IMAGES - parseRefImages(project?.charProjectRefImages).length),
      scene: Math.max(0, MAX_TOTAL_REF_IMAGES - parseRefImages(project?.sceneProjectRefImages).length),
      prop: Math.max(0, MAX_TOTAL_REF_IMAGES - parseRefImages(project?.propProjectRefImages).length),
    };
    setSearchSelectModal({ elementType, elementName, state: '__base__', currentRefCount, maxTotal: refCountMap[elementType] ?? 0 });
    setSearchSelectedUrls([]);
    setSearchLightboxIndex(null);
  };

  const handleCharAddState = (charName: string, state: string, desc: string, episodes: number[] = []) => {
    setChars(prev => [...prev, newCharacterState(charName, state, desc, episodes)]);
  };

  const handleSceneAddState = (sceneName: string, state: string, desc: string, episodes: number[] = []) => {
    setScenes(prev => [...prev, newSceneState(sceneName, state, desc, episodes)]);
  };

  const handlePropAddState = (propName: string, state: string, desc: string, episodes: number[] = []) => {
    setProps(prev => [...prev, newPropState(propName, state, desc, episodes)]);
  };

  const handleAddNewElement = async (
    elementType: 'character' | 'scene' | 'prop',
    name: string,
    desc: string,
    episodes: number[],
    setAdding: React.Dispatch<React.SetStateAction<AddingNewElement | null>>,
  ) => {
    if (!id || !name.trim()) return;
    setAdding(prev => prev ? { ...prev, loading: true } : null);
    try {
      await addAnalysisElement(id, elementType, name.trim(), '', desc.trim(), episodes);
      if (elementType === 'character') {
        setChars(prev => [...prev, { name: name.trim(), state: '', appearance: desc.trim(), personality: '', imagePrompt: '', episodes }]);
      } else if (elementType === 'scene') {
        setScenes(prev => [...prev, { name: name.trim(), state: '', description: desc.trim(), atmosphere: '', imagePrompt: '', episodes }]);
      } else {
        setProps(prev => [...prev, { name: name.trim(), state: '', description: desc.trim(), imagePrompt: '', episodes }]);
      }
      setAdding(null);
    } catch (err: any) {
      toast.error(err.message || t('addElement.addFailed'));
      setAdding(prev => prev ? { ...prev, loading: false } : null);
    }
  };

  const handleStartAnalysis = async () => {
    if (!id) return;
    setRetryingAnalysis(true);
    try {
      await retryAnalysis(id);
      setAnalysis(prev => prev
        ? { ...prev, status: 'analyzing' as const }
        : { id: '', scriptId: '', status: 'analyzing' as const, characters: '[]', scenes: '[]', props: '[]', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() });
    } catch (err: any) {
      toast.error(err.message || t('analysis.startFailed'));
    } finally {
      setRetryingAnalysis(false);
    }
  };

  const handleReanalyzeConfirm = async () => {
    if (confirmReanalyzeStep === 1) {
      setConfirmReanalyzeStep(2);
      return;
    }
    // Step 2 confirmed
    if (!id) return;
    setRetryingAnalysis(true);
    setConfirmReanalyzeOpen(false);
    setConfirmReanalyzeStep(1);
    try {
      await retryAnalysis(id);
      setAnalysis(prev => prev ? { ...prev, status: 'analyzing' } : prev);
    } catch (err: any) {
      toast.error(err.message || t('confirmDialogs.reanalyze.reanalysisFailed'));
    } finally {
      setRetryingAnalysis(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <EmptyState
          icon=""
          description={t('loading', { ns: 'common' })}
          action={<div className="loading-spinner" style={{ width: '32px', height: '32px', borderWidth: '3px', margin: '0 auto' }}></div>}
        />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="container">
        <EmptyState
          icon="&#10067;"
          title={t('project.notExist')}
          action={<Link to="/projects" className="btn btn-primary">{t('project.backToList')}</Link>}
        />
      </div>
    );
  }

  const mode: AnalysisMode = project.analysisMode ?? 'basic';

  return (
    <div className="container">
      {/* Title 栏：基础信息（剧本上传面板已迁至「剧本信息」页 ScriptSummaryPage） */}
      <div className="project-detail-top" style={{ display: 'flex', gap: '0.75rem', alignItems: 'stretch', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <div className="card project-info-panel theme-left-border" style={{ flex: '1 1 auto', width: '100%', borderRadius: '10px', padding: '1rem', margin: 0, display: 'flex', gap: '1rem', '--_theme-color': 'var(--theme-project)', '--_theme-color-end': '#a78bfa' } as React.CSSProperties}>
          <ProjectInfoSummary project={project} t={t} />
        </div>
      </div>

      {mode === 'external' && id && (
        <ExternalAnalysisSection
          project={project}
          projectId={id}
          analysis={analysis}
          episodes={episodes}
          externalTaskId={externalTaskId}
          lastFailedExternalTaskId={lastFailedExternalTaskId}
          lastFailedExternalReason={lastFailedExternalReason}
          t={t}
          toast={toast}
          setExternalTaskId={setExternalTaskId}
          setLastFailedExternalTaskId={setLastFailedExternalTaskId}
          setLastFailedExternalReason={setLastFailedExternalReason}
          loadProject={loadProject}
        />
      )}

      <ProjectAnalysisPanel
        mode={mode}
        id={id}
        analysis={analysis}
        analysisAuthorityRevision={analysisAuthorityRevision}
        project={project}
        activeImageModels={cfg.bootstrap.activeImageModels}
        characterImageWorkflow={project?.characterImageWorkflow}
        currentUser={currentUser}
        analysisTab={analysisTab}
        setAnalysisTab={setAnalysisTab}
        analysisExpanded={true}
        t={t}
        episodes={episodes}
        handleStartAnalysis={handleStartAnalysis}
        retryingAnalysis={retryingAnalysis}
        setRetryingAnalysis={setRetryingAnalysis}
        cancellingAnalysis={cancellingAnalysis}
        setCancellingAnalysis={setCancellingAnalysis}
        setAnalysis={setAnalysis}
        toast={toast}
        charGroups={charGroups}
        characterVoiceMap={characterVoiceMap}
        sceneGroups={sceneGroups}
        propGroups={propGroups}
        setAssetShareForm={setAssetShareForm}
        setAssetShareModal={setAssetShareModal}
        openShareHistory={openShareHistory}
        searchIsActive={searchIsActive}
        isGeneratingKeywords={isGeneratingKeywords}
        placeholderTimedOut={placeholderTimedOut}
        searchActiveCount={searchActiveCount}
        searchQueuedCount={searchQueuedCount}
        searchingCount={searchingCount}
        searchSummary={searchSummary}
        searchTasks={searchTasks}
        triggeringSearch={triggeringSearch}
        setConfirmResearchOpen={setConfirmResearchOpen}
        setConfirmCategorySearch={setConfirmCategorySearch}
        searchDetailExpanded={searchDetailExpanded}
        activeSearchDetails={activeSearchDetails}
        setConfirmBatchSearch={setConfirmBatchSearch}
        isMobile={isMobile}
        filteredCharGroups={filteredCharGroups}
        filteredSceneGroups={filteredSceneGroups}
        filteredPropGroups={filteredPropGroups}
        allAssetEpisodes={allAssetEpisodes}
        filterEpisodes={filterEpisodes}
        setFilterEpisodes={setFilterEpisodes}
        assetSortMode={assetSortMode}
        setAssetSortMode={setAssetSortMode}
        props={props}
        activeNavId={activeNavId}
        mobileDrawerAsset={mobileDrawerAsset}
        setMobileDrawerAsset={setMobileDrawerAsset}
        activeTaskMap={activeTaskMap}
        failedTaskMap={failedTaskMap}
        baseActiveTaskMap={baseActiveTaskMap}
        baseFailedTaskMap={baseFailedTaskMap}
        onImageTaskResolved={handleImageTaskResolved}
        episodeMap={episodeMap}
        loadAnalysis={loadAnalysis}
        openSearchSelectModal={openSearchSelectModal}
        getSearchImageCount={getSearchImageCount}
        openSearchSelectModalAllStates={openSearchSelectModalAllStates}
        getSearchImageCountAllStates={getSearchImageCountAllStates}
        getSearchTaskProgress={getSearchTaskProgress}
        getSearchTaskProgressAllStates={getSearchTaskProgressAllStates}
        handleElementUpdate={handleElementUpdate}
        handleCharAddState={handleCharAddState}
        handleSceneAddState={handleSceneAddState}
        handlePropAddState={handlePropAddState}
        disableAssetLoading={disableAssetLoading}
        setDisableAssetLoading={setDisableAssetLoading}
        setDisableAssetPending={setDisableAssetPending}
        setDisableAssetAffectedCount={setDisableAssetAffectedCount}
        setDisableAssetAffectedDetails={setDisableAssetAffectedDetails}
        setDisableAssetStep={setDisableAssetStep}
        setProject={setProject}
        setRenameTarget={setRenameTarget}
        setCommentTarget={setCommentTarget}
        pendingCommentCountMap={pendingCommentCountMap}
        isRealPerson={isRealPerson}
        reviewStatusMap={reviewStatusMap}
        loadProjectReviews={loadProjectReviews}
        availableEpisodeOptions={availableEpisodeOptions}
        handleElementEpisodesUpdate={handleElementEpisodesUpdate}
        handleAddNewElement={handleAddNewElement}
        addingNewChar={addingNewChar}
        setAddingNewChar={setAddingNewChar}
        addingNewScene={addingNewScene}
        setAddingNewScene={setAddingNewScene}
        addingNewProp={addingNewProp}
        setAddingNewProp={setAddingNewProp}
        onGenerateKeywords={handleGenerateKeywords}
        getKeywordStatus={getKeywordStatus}
        onSubmitSearch={handleSubmitSearch}
      />


      {/* 剧本上传面板已迁至「剧本信息」页；资产页仅保留硬强制 Step4 入口（缺剧本时强制上传）。 */}
      <ScriptFormatGuideModal
        open={needsScript}
        dismissible={false}
        uploading={forcedUploading}
        t={t}
        onClose={() => { /* 硬强制：不可空关 */ }}
        onSelectFile={(file) => handleForcedScriptUpload(file)}
        onInvalidFile={(messageKey = 'script.fileFormatOnly') => toast.warning(t(messageKey))}
      />

      <EditProjectDialog
        cfg={cfg}
        open={isEditing && !!project}
        onClose={() => setIsEditing(false)}
        analysisStarted={!!analysis}
      />

      <StyleConfigDialog
        cfg={cfg}
        projectId={id}
        open={(isBaseConfigOpen || needsStyle) && !!project}
        dismissible={!needsStyle}
        onClose={() => setIsBaseConfigOpen(false)}
      />

      <ProjectConfirmDialogs
        projectId={id}
        t={t}
        toast={toast}
        confirmGenPromptsOpen={confirmGenPromptsOpen}
        confirmReanalyzeOpen={confirmReanalyzeOpen}
        confirmResearchOpen={confirmResearchOpen}
        confirmCategorySearch={confirmCategorySearch}
        confirmBatchSearch={confirmBatchSearch}
        confirmReanalyzeStep={confirmReanalyzeStep}
        analysisStatus={analysis?.status}
        analysisIsAdvanced={analysis?.isAdvanced}
        searchSummary={searchSummary}
        generatingPrompts={generatingPrompts}
        retryingAnalysis={retryingAnalysis}
        setConfirmGenPromptsOpen={setConfirmGenPromptsOpen}
        setConfirmReanalyzeOpen={setConfirmReanalyzeOpen}
        setConfirmReanalyzeStep={setConfirmReanalyzeStep}
        setConfirmResearchOpen={setConfirmResearchOpen}
        setConfirmCategorySearch={setConfirmCategorySearch}
        setConfirmBatchSearch={setConfirmBatchSearch}
        setGeneratingPrompts={setGeneratingPrompts}
        loadAnalysis={loadAnalysis}
        handleReanalyzeConfirm={handleReanalyzeConfirm}
        handleTriggerSearch={handleTriggerSearch}
        handleBatchSearchConfirmed={handleBatchSearchConfirmed}
      />

      <ProjectDetailAuxiliaryModals
        project={project}
        projectId={id}
        t={t}
        toast={toast}
        disableAssetPending={disableAssetPending}
        disableAssetStep={disableAssetStep}
        disableAssetAffectedCount={disableAssetAffectedCount}
        disableAssetAffectedDetails={disableAssetAffectedDetails}
        disableAssetLoading={disableAssetLoading}
        setDisableAssetPending={setDisableAssetPending}
        setDisableAssetStep={setDisableAssetStep}
        setDisableAssetLoading={setDisableAssetLoading}
        setProject={setProject}
        renameTarget={renameTarget}
        setRenameTarget={setRenameTarget}
        commentTarget={commentTarget}
        setCommentTarget={setCommentTarget}
        reloadAssetComments={reloadAssetComments}
        loadAnalysis={loadAnalysis}
        loadProject={loadProject}
        searchSelectModal={searchSelectModal}
        searchSelectGroups={searchSelectGroups}
        searchSelectedUrls={searchSelectedUrls}
        savingSearchSelection={savingSearchSelection}
        searchLightboxIndex={searchLightboxIndex}
        searchSelectImages={searchSelectImages}
        setSearchSelectModal={setSearchSelectModal}
        setSearchSelectedUrls={setSearchSelectedUrls}
        handleConfirmSearchSelect={handleConfirmSearchSelect}
        setSearchLightboxIndex={setSearchLightboxIndex}
        setSearchTasks={setSearchTasks}
        assetShareModal={assetShareModal}
        assetShareForm={assetShareForm}
        assetShareLoading={assetShareLoading}
        setAssetShareModal={setAssetShareModal}
        setAssetShareForm={setAssetShareForm}
        handleCreateAssetShare={handleCreateAssetShare}
        showShareHistory={showShareHistory}
        assetShareHistory={assetShareHistory}
        shareHistoryLoading={shareHistoryLoading}
        shareHistoryError={shareHistoryError}
        selectedShareLink={selectedShareLink}
        selectedShareComments={selectedShareComments}
        selectedShareImageMap={selectedShareImageMap}
        shareCommentsLoading={shareCommentsLoading}
        shareCommentsError={shareCommentsError}
        closeShareHistory={closeShareHistory}
        backToShareHistory={backToShareHistory}
        loadShareComments={loadShareComments}
        copyAssetShareLink={copyAssetShareLink}
      />
    </div>
  );
};

export default ProjectDetail;
